/**
 * Marks the passage an issue points at inside rendered section HTML
 * (spec 29 §0.6.4: amber 12% wash on the passage, marker in the margin).
 *
 * Matches within a single text node, whitespace-insensitive. Passages that
 * span an inline element boundary are not matched — the section still gets
 * its margin marker, so nothing is lost, only the wash.
 */
const MARK_CLASS = 'rounded-sm bg-[hsl(var(--status-warning)/0.12)]'

export function collapse(s: string): string {
  return s.replace(/\s+/g, ' ').trim().toLowerCase()
}

/** Returns the mark it created, or null when the passage is not present. */
export function highlightPassage(container: HTMLElement, excerpt: string): HTMLElement | null {
  const needle = collapse(excerpt)
  if (needle.length === 0) return null
  const walker = container.ownerDocument.createTreeWalker(container, NodeFilter.SHOW_TEXT)
  for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
    const raw = node.data
    // Build a map from collapsed index → raw index so the range lands on the real characters.
    const map: number[] = []
    let collapsed = ''
    let lastWasSpace = true
    for (let i = 0; i < raw.length; i++) {
      const ch = raw[i]!
      if (/\s/.test(ch)) {
        if (!lastWasSpace) {
          collapsed += ' '
          map.push(i)
        }
        lastWasSpace = true
      } else {
        collapsed += ch.toLowerCase()
        map.push(i)
        lastWasSpace = false
      }
    }
    const at = collapsed.indexOf(needle)
    if (at === -1) continue
    const start = map[at]!
    const end = map[at + needle.length - 1]! + 1
    const range = container.ownerDocument.createRange()
    range.setStart(node, start)
    range.setEnd(node, end)
    const mark = container.ownerDocument.createElement('mark')
    mark.setAttribute('data-issue-highlight', '')
    mark.className = MARK_CLASS
    range.surroundContents(mark)
    return mark
  }
  return null
}

export function clearHighlights(container: HTMLElement): void {
  container.querySelectorAll('mark[data-issue-highlight]').forEach((mark) => {
    const parent = mark.parentNode
    if (!parent) return
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark)
    parent.removeChild(mark)
    parent.normalize()
  })
}
